import React, { useEffect, useState } from 'react';
import { landlordAPI, badgeAPI } from '../../lib/api';
import { formatDateTime } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { HeadphonesIcon, Plus, Loader2, Send, MessageSquare, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { AttachmentUploader, AttachmentPreview } from '../../components/support/SupportAttachment';

export default function LandlordSupport() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyAttachment, setReplyAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [newTicketAttachment, setNewTicketAttachment] = useState(null);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    message: '',
    priority: 'normal'
  });

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      const response = await landlordAPI.getSupportTickets();
      setTickets(response.data);
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSending(true);
    try {
      await landlordAPI.createSupportTicket({ ...newTicket, ...(newTicketAttachment || {}) });
      toast.success('Support ticket created');
      setCreateDialogOpen(false);
      setNewTicket({ subject: '', message: '', priority: 'normal' });
      setNewTicketAttachment(null);
      loadTickets();
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setSending(false);
    }
  };

  const handleOpenTicket = async (ticket) => {
    setSelectedTicket(ticket);
    if (ticket.unread_for_owner) {
      try {
        await badgeAPI.markSupportRead(ticket.ticket_id);
        setTickets((prev) => prev.map((t) => (t.ticket_id === ticket.ticket_id ? { ...t, unread_for_owner: false } : t)));
        window.dispatchEvent(new Event('badge-refresh'));
      } catch (err) {
        // Non-critical: marking a ticket read shouldn't block opening it
        console.error('Failed to mark support ticket as read:', err);
      }
    }
  };

  const handleSendReply = async () => {
    if ((!replyText.trim() && !replyAttachment) || !selectedTicket) return;

    setSending(true);
    try {
      await landlordAPI.replyToSupportTicket(selectedTicket.ticket_id, {
        message: replyText,
        ...(replyAttachment || {}),
      });
      toast.success('Reply sent');
      setReplyText('');
      setReplyAttachment(null);
      // Refresh the ticket
      const response = await landlordAPI.getSupportTickets();
      const updated = response.data.find(t => t.ticket_id === selectedTicket.ticket_id);
      if (updated) setSelectedTicket(updated);
      loadTickets();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <AlertCircle className="w-4 h-4" />;
      case 'in_progress': return <Clock className="w-4 h-4" />;
      case 'resolved': return <CheckCircle className="w-4 h-4" />;
      case 'closed': return <CheckCircle className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300';
      case 'in_progress': return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300';
      case 'resolved': return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';
      case 'closed': return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
      default: return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="landlord-support-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold">Support</h1>
          <p className="text-muted-foreground mt-1">Get help from our support team</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} className="bg-[#C85A42] hover:bg-[#B04A35]">
          <Plus className="w-4 h-4 mr-2" />
          New Ticket
        </Button>
      </div>

      {/* Tickets List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HeadphonesIcon className="w-5 h-5" />
            My Support Tickets
          </CardTitle>
          <CardDescription>View and manage your support requests</CardDescription>
        </CardHeader>
        <CardContent>
          {tickets.length === 0 ? (
            <div className="text-center py-12">
              <HeadphonesIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No support tickets yet</p>
              <Button onClick={() => setCreateDialogOpen(true)} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Ticket
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket) => (
                <div
                  key={ticket.ticket_id}
                  className="p-4 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => handleOpenTicket(ticket)}
                  data-testid={`ticket-${ticket.ticket_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {ticket.unread_for_owner && (
                          <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" data-testid={`ticket-unread-${ticket.ticket_id}`} title="New reply" />
                        )}
                        <p className={`truncate ${ticket.unread_for_owner ? 'font-bold' : 'font-medium'}`}>{ticket.subject}</p>
                        {ticket.created_by_admin && (
                          <Badge variant="default" className="text-xs shrink-0" data-testid="from-support-badge">From Support</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-1 mt-1">{ticket.message}</p>
                      <p className="text-xs text-muted-foreground mt-2">{formatDateTime(ticket.created_at)}</p>
                    </div>
                    <Badge className={`${getStatusColor(ticket.status)} shrink-0`}>
                      {getStatusIcon(ticket.status)}
                      <span className="ml-1 capitalize">{ticket.status.replace('_', ' ')}</span>
                    </Badge>
                  </div>
                  {ticket.replies?.length > 0 && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                      <MessageSquare className="w-4 h-4" />
                      {ticket.replies.length} {ticket.replies.length === 1 ? 'reply' : 'replies'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Ticket Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Support Ticket</DialogTitle>
            <DialogDescription>
              Describe your issue and our team will get back to you as soon as possible
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Subject *</Label>
              <Input
                value={newTicket.subject}
                onChange={(e) => setNewTicket(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Brief description of your issue"
                data-testid="ll-new-ticket-subject"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={newTicket.priority} onValueChange={(val) => setNewTicket(prev => ({ ...prev, priority: val }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea
                value={newTicket.message}
                onChange={(e) => setNewTicket(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Describe your issue in detail..."
                rows={5}
                data-testid="ll-new-ticket-message"
              />
            </div>

            <div className="space-y-2">
              <Label>Attachment (optional)</Label>
              <div>
                <AttachmentUploader
                  attachment={newTicketAttachment}
                  onChange={setNewTicketAttachment}
                  uploadFn={landlordAPI.uploadSupportAttachment}
                  testidPrefix="ll-new-ticket"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateTicket} disabled={sending} className="bg-[#C85A42] hover:bg-[#B04A35]" data-testid="ll-create-ticket-submit">
              {sending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Create Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={(open) => !open && setSelectedTicket(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          {selectedTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">{selectedTicket.subject}</DialogTitle>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className={getStatusColor(selectedTicket.status)}>
                    {getStatusIcon(selectedTicket.status)}
                    <span className="ml-1 capitalize">{selectedTicket.status.replace('_', ' ')}</span>
                  </Badge>
                  <span className="text-sm text-muted-foreground">{formatDateTime(selectedTicket.created_at)}</span>
                </div>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto space-y-4 py-4">
                {/* Original Message */}
                <div className={`p-4 rounded-lg ${selectedTicket.created_by_admin ? 'bg-[#C85A42]/10' : 'bg-muted/50'}`}>
                  <Badge variant={selectedTicket.created_by_admin ? 'default' : 'secondary'} className="mb-2">
                    {selectedTicket.created_by_admin ? 'Support Team' : 'You'}
                  </Badge>
                  <p className="text-sm whitespace-pre-wrap">{selectedTicket.message}</p>
                  <AttachmentPreview
                    url={selectedTicket.attachment_url}
                    name={selectedTicket.attachment_name}
                    type={selectedTicket.attachment_type}
                    testid="ll-ticket-original-attachment"
                  />
                </div>

                {/* Replies */}
                {selectedTicket.replies?.map((reply) => (
                  <div
                    key={reply.reply_id}
                    className={`p-4 rounded-lg ${
                      reply.user_role === 'admin' 
                        ? 'bg-[#C85A42]/10 ml-8' 
                        : 'bg-muted/50 mr-8'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant={reply.user_role === 'admin' ? 'default' : 'secondary'}>
                        {reply.user_role === 'admin' ? 'Support Team' : 'You'}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{formatDateTime(reply.created_at)}</span>
                    </div>
                    {reply.message && <p className="text-sm whitespace-pre-wrap">{reply.message}</p>}
                    <AttachmentPreview url={reply.attachment_url} name={reply.attachment_name} type={reply.attachment_type} />
                  </div>
                ))}
              </div>

              {/* Reply Input */}
              {selectedTicket.status !== 'closed' && (
                <div className="border-t pt-4 space-y-3">
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply..."
                    rows={3}
                    data-testid="ll-reply-input"
                  />
                  <div className="flex justify-between items-center gap-2">
                    <AttachmentUploader
                      attachment={replyAttachment}
                      onChange={setReplyAttachment}
                      uploadFn={landlordAPI.uploadSupportAttachment}
                      testidPrefix="ll-reply"
                    />
                    <Button
                      onClick={handleSendReply}
                      disabled={sending || (!replyText.trim() && !replyAttachment)}
                      className="bg-[#C85A42] hover:bg-[#B04A35]"
                      data-testid="ll-send-reply-btn"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                      Send Reply
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
